/**
 * Entra ID sign-in page for Silk.Shuttle.
 *
 * Replaces the guest sign-in that previously let anyone straight into the
 * portal. Every visitor now authenticates against the gtm-demo-backstage app
 * registration in Entra ID before the app renders.
 *
 * WHY THIS FILE EXISTS AT ALL
 * ───────────────────────────
 * On the classic Backstage frontend you pass `components={{ SignInPage }}` to
 * createApp. This app runs the new (alpha) frontend system, where createApp
 * takes `features` and has no components option — so the sign-in page has to be
 * contributed as an extension instead.
 *
 * The blueprint for that is SignInPageBlueprint. Note it is exported from
 * @backstage/plugin-app-react, NOT from @backstage/frontend-plugin-api — the
 * blueprint's own docstring says frontend-plugin-api, but it is not exported
 * there (checked against 0.16.2). Importing from the wrong package is the most
 * likely reason someone concludes the new frontend system "can't do sign-in".
 *
 * `auto` attempts a silent sign-in first, so a returning user with a live session
 * goes straight through. When there is no session to restore it falls back to
 * rendering the provider card below, which is what a first-time visitor sees.
 *
 * WHY THE THEME IS APPLIED HERE
 * ─────────────────────────────
 * The sign-in page renders BEFORE the app's theme extensions are live, so it
 * cannot rely on appThemeApi — and modules/theme/forcer.tsx cannot help either,
 * because that runs inside AppRootWrapper, which is downstream of this page.
 * Left alone, Backstage falls back to its built-in dark theme: a neutral gray
 * canvas and a teal page banner. That is the unbranded login screen.
 *
 * Wrapping in the app's own ShuttleThemeProvider fixes it at the root
 * rather than by patching colours one at a time: the banner gradient, paper
 * colour, typography and primary colour all come from the same tokens the rest of
 * the portal uses, so the login page cannot drift from the app's branding.
 */

import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core';
import type { PropsWithChildren } from 'react';
// ShuttleThemeProvider, not UnifiedThemeProvider plus the raw theme: the theme
// module already builds this exact composition for its ThemeBlueprint, so sharing it
// keeps ONE definition of how the Silk theme is applied and the login screen cannot
// drift from the app.
import { ShuttleThemeProvider } from '../theme';

// Palette values are duplicated from modules/theme deliberately: these rules are
// injected as @global CSS to reach inside Backstage's own SignInPage markup, which
// does not read theme tokens for its layout. The theme provider handles everything
// that CAN be themed; this handles what cannot.
const CANVAS = '#0F0721';
const SURFACE = '#0A1128';
const PRIMARY = '#FF286E';
const PRIMARY_LIGHT = '#FF5A8E';

const useSignInStyles = makeStyles(
  {
    '@global': {
      // Backstage's SignInPage renders a full Page with its own Header banner.
      // On a login screen that banner is noise - it repeats the product name
      // directly above the Silk lockup. Hidden rather than restyled.
      '.signin-root header': {
        display: 'none !important',
      },
      // The default grid tucks the card into the top-left of the content area.
      // Centring it is what makes this read as a login screen rather than a
      // dashboard that happens to have one card on it.
      '.signin-root [class*="MuiGrid-container"]': {
        justifyContent: 'center !important',
      },
      '.signin-root [class*="MuiCard-root"]': {
        backgroundColor: `${SURFACE} !important`,
        border: `1px solid rgba(255, 40, 110, 0.25) !important`,
        borderRadius: '12px !important',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.55) !important',
        maxWidth: '420px',
      },
      // The card header carries the title, which is now "Silk Demo Portal" rather
      // than "Microsoft" - so it is shown, and centred to match the button below.
      // Naming the identity provider on a login screen tells the user nothing
      // useful: there is one provider, and which IdP sits behind it is our
      // implementation detail, not their decision.
      '.signin-root [class*="MuiCardHeader-root"]': {
        textAlign: 'center',
        padding: '24px 28px 4px !important',
      },
      '.signin-root [class*="MuiCardHeader-title"]': {
        fontSize: '1.25rem !important',
        fontWeight: 700,
        color: '#FFFFFF !important',
      },
      // message is empty, so this would otherwise render as a tall empty box
      // between the title and the button.
      '.signin-root [class*="MuiCardContent-root"]': {
        padding: '4px 28px 0 !important',
        textAlign: 'center',
      },
      '.signin-root [class*="MuiCardActions-root"]': {
        justifyContent: 'center !important',
        padding: '8px 28px 28px !important',
      },
      // Default is a low-contrast text button. Made a solid fuchsia call to
      // action, since it is the only actionable element on the page.
      '.signin-root [class*="MuiButton-root"]': {
        backgroundColor: `${PRIMARY} !important`,
        color: '#FFFFFF !important',
        fontWeight: 700,
        letterSpacing: '0.04em',
        padding: '10px 32px !important',
        borderRadius: '8px !important',
      },
      '.signin-root [class*="MuiButton-root"]:hover': {
        backgroundColor: `${PRIMARY_LIGHT} !important`,
      },
    },
  },
  { name: 'SilkSignIn' },
);

const useBrandStyles = makeStyles({
  root: {
    minHeight: '100vh',
    backgroundColor: CANVAS,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  shuttle: {
    width: 'clamp(120px, 18vw, 200px)',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
  },
  // Same lockup convention as the sidebar LogoFull: "Silk" + fuchsia dot +
  // suffix. Kept identical so the login screen and the app agree.
  wordmark: {
    fontWeight: 700,
    fontSize: 'clamp(2rem, 4vw, 2.75rem)',
    letterSpacing: '0.02em',
    color: '#FFFFFF',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  dot: {
    color: PRIMARY,
  },
  // Keeps the card from stretching across a wide monitor.
  card: {
    width: '100%',
    maxWidth: '480px',
  },
});

const SignInLayout = (props: PropsWithChildren<{}>) => {
  useSignInStyles();
  const classes = useBrandStyles();

  return (
    <div className={`${classes.root} signin-root`}>
      <div className={classes.brand}>
        <img src="/shuttle.png" alt="" className={classes.shuttle} />
        <span className={classes.wordmark}>
          Silk<span className={classes.dot}>.</span>Shuttle
        </span>
      </div>
      <div className={classes.card}>{props.children}</div>
    </div>
  );
};

const microsoftSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      const { SignInPage } = await import('@backstage/core-components');
      return props => (
        <ShuttleThemeProvider>
          <SignInLayout>
            <SignInPage
              {...props}
              auto
              provider={{
                id: 'microsoft-auth-provider',
                // Deliberately NOT "Microsoft". Which identity provider sits behind
                // the button is an implementation detail, and "Silk account" was
                // ambiguous - people have Silk logins for several systems. The
                // portal names itself and gets out of the way.
                title: 'Silk Demo Portal',
                // Empty on purpose: the title says where you are and the button says
                // what to do. A sentence between them restated both.
                message: '',
                apiRef: microsoftAuthApiRef,
              }}
            />
          </SignInLayout>
        </ShuttleThemeProvider>
      );
    },
  },
});

export const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [microsoftSignInPage],
});
